/// <amd-module name="Repos/Views/Import/Components/PanelCreate/PanelCreate" />
import * as React from "react";
import { IProjectRepositoryInformationService } from "Repos/Common/ProjectRepositoryInformationService";
import { RepositoryType } from "Repos/Common/Types";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { initializeRepository } from "Repos/Views/Import/Api";
import * as Resources from "Repos/Views/Import/Resources";
import { getGitClient } from "Repos/Views/Import/RestClient/Git";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListSelection, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { PanelCreateProps } from "./PanelCreate.Props";

export interface PanelCreateState {
    createStarted?: boolean;
    messageFailure?: React.ReactNode;
    readme: boolean;
    repositoryName?: string;
}

export class PanelCreate extends VssComponent<PanelCreateProps, PanelCreateState> {
    private defaultGitBranchName = new ObservableValue<string>("");
    private initializeItems: ObservableArray<IListBoxItem>;
    private initializeSelection: ListSelection;
    private providers: IListBoxItem[] = [];
    private providerTypes: RepositoryType[] = [];
    private providerSelection = new ListSelection({
        selectedRanges: [{ beginIndex: 0, endIndex: 0 }],
        selectOnFocus: false
    });

    constructor(props: PanelCreateProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            readme: true
        };

        // Setup the items for the initialization dropdown.
        this.initializeItems = new ObservableArray<IListBoxItem>([{ id: "none", text: Resources.None }]);
        this.initializeSelection = new ListSelection({
            selectedRanges: [{ beginIndex: 0, endIndex: 0 }],
            selectOnFocus: false
        });

        const vcViewModelDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const vcViewModel = vcViewModelDataProviderService.getViewModel();

        // Determine which version control providers we will allow.
        this.providers.push({ iconProps: { iconName: "GitLogo" }, id: RepositoryType.git, text: "Git" });
        this.providerTypes.push(RepositoryType.git);

        if (!vcViewModel.projectVersionControlInfo.supportsTFVC) {
            this.providers.push({ iconProps: { iconName: "TFVCLogo" }, id: RepositoryType.tfvc, text: "TFVC" });
            this.providerTypes.push(RepositoryType.tfvc);
        }

        // Load the set of additional items asynchronously.
        const gitClient = getGitClient(context.pageContext);
        gitClient.getTemplateList(this.props.projectName, "gitignore").then(templates => {
            this.initializeItems.push(
                ...templates.map(template => {
                    return { id: template.name, text: template.name.split(".", 1)[0] };
                })
            );
        });

        const projectInfoService = context.pageContext.getService<IProjectRepositoryInformationService>("IProjectRepositoryInformationService");
        // Always fetch (ignoreCache: true) because default branch may have changed since the page was loaded (in the Settings tab).
        projectInfoService.getDataAsync(true).then(projectInfo => {
            this.defaultGitBranchName.value = projectInfo?.defaultGitBranchName || "";
        });
    }

    public render() {
        return (
            <Observer
                providerSelection={{
                    observableValue: this.providerSelection,
                    filter: (value: ISelectionRange[], action: string) => {
                        if (action === "select") {
                            const providerType = this.providerTypes[value[0].beginIndex];

                            this.setState({
                                messageFailure: undefined,
                                readme: true,
                                repositoryName: providerType === RepositoryType.tfvc ? "$/" + this.props.projectName : undefined
                            });
                        }

                        return false;
                    }
                }}
            >
                {() => {
                    const { repositoryName } = this.state;
                    const providerType = this.providerTypes[this.providerSelection.value[0].beginIndex];
                    const createDisabled = providerType === RepositoryType.git && !repositoryName;
                    const typeDisabled = this.providers.length === 1;

                    return (
                        <Panel
                            defaultActiveElement={typeDisabled ? ".repository-name" : ".repository-type"}
                            footerButtonProps={[
                                { text: Resources.Cancel, onClick: () => this.props.onDismiss() },
                                {
                                    disabled: createDisabled,
                                    primary: true,
                                    onClick: this.onCreate,
                                    text: Resources.Create
                                }
                            ]}
                            lightDismiss={false}
                            onDismiss={this.props.onDismiss}
                            overlayContent={
                                this.state.createStarted ? <Spinner label={Resources.CreatingRepository} size={SpinnerSize.large} /> : null
                            }
                            titleProps={{ text: Resources.CreateRepository }}
                        >
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                {this.state.messageFailure ? (
                                    <MessageCard onDismiss={() => this.setState({ messageFailure: undefined })} severity={MessageCardSeverity.Error}>
                                        <div className="flex-column font-size-ms">{this.state.messageFailure}</div>
                                    </MessageCard>
                                ) : null}
                                <FormItem label={Resources.RepoTypeLabel}>
                                    <Dropdown
                                        className="repository-type"
                                        disabled={typeDisabled}
                                        items={this.providers}
                                        selection={this.providerSelection}
                                    />
                                </FormItem>
                                <FormItem ariaLabel={Resources.RepoNameAriaLabel} label={Resources.RepoNameLabel}>
                                    <TextField
                                        disabled={providerType === RepositoryType.tfvc}
                                        inputClassName="repository-name"
                                        onChange={(e, value) => this.setState({ repositoryName: value })}
                                        placeholder={Resources.RepoNamePlaceholder}
                                        value={this.state.repositoryName}
                                    />
                                </FormItem>
                                {providerType === RepositoryType.git ? (
                                    <>
                                        <Checkbox
                                            checked={this.state.readme}
                                            className="margin-top-8"
                                            label={Resources.AddReadMe}
                                            onChange={(event, checked) => this.setState({ readme: checked })}
                                        />
                                        <Dropdown
                                            className="panel-create-gitignore-dropdown"
                                            items={this.initializeItems}
                                            renderExpandable={props => (
                                                <DropdownExpandableButton
                                                    {...props}
                                                    renderSelectedItems={(selection: IListSelection, items: IListBoxItem[]) => {
                                                        const item = items[selection.value[0].beginIndex];

                                                        return (
                                                            <>
                                                                <span className="secondary-text">{format(Resources.AddAnItem, ".gitignore")}</span>
                                                                {item.text}
                                                            </>
                                                        );
                                                    }}
                                                />
                                            )}
                                            selection={this.initializeSelection}
                                        />
                                        <Observer defaultGitBranchName={this.defaultGitBranchName} initializeSelection={this.initializeSelection}>
                                            {() =>
                                                (this.state.readme || this.getSelectedInitializeItem().id !== "none") && (
                                                    <FormatComponent className="margin-16" format={Resources.InitializeWithDefaultBranch}>
                                                        <span className="monospaced-text">
                                                            <Icon iconName="OpenSource" />
                                                            {this.defaultGitBranchName.value}
                                                        </span>
                                                    </FormatComponent>
                                                )
                                            }
                                        </Observer>
                                    </>
                                ) : (
                                    <>
                                        <span>{Resources.TFVCReminder}</span>
                                        <Link href="https://go.microsoft.com/fwlink/?LinkId=699398" target="_blank">
                                            {Resources.LearnMore}
                                        </Link>
                                    </>
                                )}
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private onCreate = () => {
        const initializeItem = this.getSelectedInitializeItem();
        const providerIndex = this.providerSelection.value[0].beginIndex;

        const createOperation = initializeRepository(this.context.pageContext, {
            application: initializeItem.id !== "none" ? { id: initializeItem.id, name: initializeItem.text! } : undefined,
            createRepository: true,
            defaultGitBranchName: this.defaultGitBranchName.value,
            projectId: this.props.projectId,
            projectName: this.props.projectName,
            readme: this.state.readme,
            repositoryName: this.state.repositoryName!,
            type: providerIndex === 0 ? RepositoryType.git : RepositoryType.tfvc
        });

        createOperation.catch(error => {
            this.setState({ createStarted: false, messageFailure: error });
        });

        this.props.onCreate && this.props.onCreate(createOperation);
        this.setState({ createStarted: true });
    };

    private getSelectedInitializeItem() {
        const initializeIndex = this.initializeSelection.value[0].beginIndex;
        return this.initializeItems.value[initializeIndex];
    }
}

Components.add("ms.vss-code-web.panel-create", PanelCreate);
